import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form, Button, Typography, Row, Col, Input, Select, Space, Tooltip,
  Card, Table, InputNumber, Modal, Alert, Spin, App as AntApp,
} from 'antd';
import {
  SaveOutlined, InfoCircleOutlined, PlusOutlined,
  DeleteOutlined, ArrowLeftOutlined,
} from '@ant-design/icons';
import ControlSelect from '../controls/ControlSelect';
import AddButton from '../controls/AddButton';
import AddModal from '../controls/AddModal';
import api from '../services/api';

const { Title, Text } = Typography;

const ProductoEditar = () => {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const { modal: antModal } = AntApp.useApp();
  const [form]       = Form.useForm();
  const [convForm]   = Form.useForm();

  const selectedCategoria    = Form.useWatch('ddCategoria', form);
  const selectedTipoPlanDesc = Form.useWatch('ddTipoPlanDescuento', form);
  const showPlanDesc     = Number(selectedCategoria) === 6;
  const showPresentacion = Number(selectedTipoPlanDesc) === 2;

  // ── Estado general ───────────────────────────────────────
  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving]           = useState(false);
  const [producto, setProducto]       = useState(null);
  const [unidadBloqueada, setUnidadBloqueada]         = useState(false);
  const [inventarioCantidad, setInventarioCantidad]   = useState(0);

  // ── Catálogos ────────────────────────────────────────────
  const [categorias, setCategorias]       = useState([]);
  const [marcas, setMarcas]               = useState([]);
  const [centrosCosto, setCentrosCosto]   = useState([]);
  const [unidadesAgrupadas, setUnidadesAgrupadas] = useState([]);  // para el Select agrupado
  const [unidadesFlat, setUnidadesFlat]   = useState([]);          // para el modal conversión
  const [unidadTipoMap, setUnidadTipoMap] = useState({});          // unitId → tipoMedidaId
  const [unidadNombreMap, setUnidadNombreMap] = useState({});      // unitId → nombre

  // ── Presentaciones (plan consumo tipo Peso) ───────────────
  const [presentaciones, setPresentaciones]             = useState([]);
  const [presentacionFactorById, setPresentacionFactorById] = useState({});

  // ── Conversiones ─────────────────────────────────────────
  const [conversiones, setConversiones]   = useState([]);
  const [convLoading, setConvLoading]     = useState(false);
  const [convModalOpen, setConvModalOpen] = useState(false);
  const [convSaving, setConvSaving]       = useState(false);

  // ── Modal agregar categoría / marca ──────────────────────
  const [addModal, setAddModal] = useState({ visible: false, tipo: '' });

  // ─────────────────────────────────────────────────────────
  const cargarConversiones = useCallback(async () => {
    try {
      setConvLoading(true);
      const res  = await api.get(`/UnidadConversion/producto/${id}`);
      const vals = res.data.$values || res.data || [];
      // Solo las específicas de este producto (excluye las genéricas null)
      setConversiones(vals.filter(c => c.idProducto === Number(id)));
    } catch (err) {
      console.error(err);
    } finally {
      setConvLoading(false);
    }
  }, [id]);

  // ── Carga inicial ─────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      try {
        setPageLoading(true);

        const [catRes, marRes, ccRes, tipoRes, unidadRes, prodRes] = await Promise.all([
          api.get('/Categoria/active'),
          api.get('/Marca/active'),
          api.get('/CentroCosto/activos'),
          api.get('/TipoMedida'),
          api.get('/UnidadMedida'),
          api.get(`/Producto/${id}`),
        ]);

        setCategorias((catRes.data.$values || catRes.data || []).map(c => ({ value: c.id, label: c.nombre })));
        setMarcas((marRes.data.$values || marRes.data || []).map(m => ({ value: m.id, label: m.nombre })));
        setCentrosCosto((ccRes.data.$values || ccRes.data || []).map(c => ({ value: c.id, label: c.nombre })));

        const tipos    = tipoRes.data.$values   || tipoRes.data   || [];
        const unidades = unidadRes.data.$values || unidadRes.data || [];

        const tipoNombreMap = {};
        tipos.forEach(t => { tipoNombreMap[t.id] = t.nombre; });

        const tipoMap    = {};
        const nomMap     = {};
        const flatArr    = [];
        const factorById = {};
        unidades.forEach(u => {
          tipoMap[u.id]    = u.idTipoMedida;
          nomMap[u.id]     = u.nombre;
          factorById[u.id] = u.factorBase ?? 1;
          const abrev = u.abreviatura ? ` (${u.abreviatura})` : '';
          flatArr.push({ value: u.id, label: `${u.nombre}${abrev}`, tipoId: u.idTipoMedida });
        });
        setUnidadTipoMap(tipoMap);
        setUnidadNombreMap(nomMap);
        setUnidadesFlat(flatArr);
        setPresentacionFactorById(factorById);

        // Opciones agrupadas por tipo (solo activas)
        const grupos = {};
        unidades.filter(u => u.estado == null || u.estado === 1).forEach(u => {
          const tipoId     = u.idTipoMedida ?? 0;
          const tipoNombre = tipoNombreMap[tipoId] || 'Otros';
          if (!grupos[tipoId]) grupos[tipoId] = { label: tipoNombre, options: [] };
          const abrev = u.abreviatura ? ` (${u.abreviatura})` : '';
          grupos[tipoId].options.push({ value: u.id, label: `${u.nombre}${abrev}` });
        });
        setUnidadesAgrupadas(Object.values(grupos));

        // Producto
        const prod = prodRes.data;
        setProducto(prod);

        // Verificar si hay inventario para bloquear la unidad
        try {
          const invRes = await api.get(`/Inventario/producto/${prod.id ?? prod.Id}`);
          const qty    = Number(invRes.data?.cantidad ?? invRes.data?.Cantidad ?? 0);
          setInventarioCantidad(qty);
          setUnidadBloqueada(qty > 0);
        } catch {
          setUnidadBloqueada(false);
        }

        // Pre-poblar formulario
        form.setFieldsValue({
          txtNombre:     prod.nombre,
          txtCodbarras:  prod.codigoBarras,
          txtCodigo:     prod.codProducto,
          ddCategoria:   prod.categoria,
          ddMarca:       prod.marca,
          ddCentroCosto: prod.idCentrosCosto,
          ddUnidadBase:  prod.unidadBaseId ?? prod.idUnidadMedida,
        });
      } catch (err) {
        console.error(err);
        antModal.error({ title: 'Error', content: 'No se pudo cargar el producto.' });
      } finally {
        setPageLoading(false);
      }
    };

    init();
    cargarConversiones();
  }, [id, form, antModal, cargarConversiones]);

  // Presentaciones: carga cuando Tipo de descuento = Peso (2)
  useEffect(() => {
    if (Number(selectedTipoPlanDesc) !== 2) {
      setPresentaciones([]);
      form.setFieldsValue({ ddPresentacion: undefined });
      return;
    }
    setPresentaciones(
      unidadesFlat.map(u => ({
        value: u.value,
        label: `${u.label} — factor: ${presentacionFactorById[u.value] ?? 1}`,
      }))
    );
  }, [selectedTipoPlanDesc, unidadesFlat, presentacionFactorById, form]);

  // ── Guardar producto ──────────────────────────────────────
  const handleSave = async (values) => {
    try {
      setSaving(true);
      const unidadBaseId = parseInt(values.ddUnidadBase, 10);
      const tipoMedidaId = unidadTipoMap[unidadBaseId] ?? producto?.tipoMedidaId ?? 0;

      await api.put(`/Producto/${id}`, {
        id:             Number(id),
        codProducto:    values.txtCodigo ? parseInt(values.txtCodigo, 10) : (producto?.codProducto ?? 0),
        categoria:      parseInt(values.ddCategoria, 10),
        nombre:         values.txtNombre,
        codigoBarras:   values.txtCodbarras || '',
        marca:          parseInt(values.ddMarca, 10),
        tipoMedidaId,
        idUnidadMedida: unidadBaseId,
        unidadBaseId,
        idCentrosCosto: parseInt(values.ddCentroCosto, 10),
        estado:         producto?.estado ?? 1,
      });

      antModal.success({ title: 'Éxito', content: 'Producto actualizado correctamente.' });
    } catch (err) {
      console.error(err);
      antModal.error({ title: 'Error', content: 'No se pudo guardar el producto.' });
    } finally {
      setSaving(false);
    }
  };

  // ── Agregar conversión ────────────────────────────────────
  const handleAddConversion = async (values) => {
    try {
      setConvSaving(true);
      const unidadBaseId = producto?.unidadBaseId ?? producto?.idUnidadMedida;
      await api.post('/UnidadConversion', {
        unidadOrigenId:  parseInt(values.ddUnidadOrigen, 10),
        unidadDestinoId: unidadBaseId,
        factor:          Number(values.factor),
        idProducto:      Number(id),
        estado:          1,
      });
      convForm.resetFields();
      setConvModalOpen(false);
      await cargarConversiones();
      antModal.success({ title: 'Éxito', content: 'Conversión agregada correctamente.' });
    } catch (err) {
      if (err?.response?.status === 409) {
        antModal.error({ title: 'Duplicado', content: 'Ya existe una conversión para ese par de unidades en este producto.' });
      } else {
        antModal.error({ title: 'Error', content: 'No se pudo guardar la conversión.' });
      }
    } finally {
      setConvSaving(false);
    }
  };

  // ── Eliminar conversión ───────────────────────────────────
  const handleDeleteConversion = (convId) => {
    antModal.confirm({
      title: 'Eliminar conversión',
      content: '¿Está seguro de eliminar esta conversión?',
      okText: 'Eliminar',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await api.delete(`/UnidadConversion/${convId}`);
          await cargarConversiones();
        } catch {
          antModal.error({ title: 'Error', content: 'No se pudo eliminar.' });
        }
      },
    });
  };

  // ── Agregar categoría / marca desde modal ─────────────────
  const handleAdd = async (values) => {
    try {
      const endpoint    = addModal.tipo === 'categoria' ? '/Categoria' : '/Marca';
      const getEndpoint = addModal.tipo === 'categoria' ? '/Categoria/active' : '/Marca/active';
      await api.post(endpoint, { nombre: values.nombre, estado: 1 });
      const res  = await api.get(getEndpoint);
      const vals = res.data.$values || res.data;
      const setter = addModal.tipo === 'categoria' ? setCategorias : setMarcas;
      setter(vals.map(x => ({ value: x.id, label: x.nombre })));
      setAddModal({ visible: false, tipo: '' });
    } catch (err) {
      console.error(err);
    }
  };

  // ─────────────────────────────────────────────────────────
  const unidadBaseId     = producto?.unidadBaseId ?? producto?.idUnidadMedida;
  const unidadBaseNombre = unidadNombreMap[unidadBaseId] ?? '—';

  const convColumns = [
    {
      title: 'Unidad de compra (origen)',
      render: (_, r) =>
        `${r.origenNombre ?? '—'}${r.origenAbrev ? ` (${r.origenAbrev})` : ''}`,
    },
    {
      title: 'Factor',
      dataIndex: 'factor',
      width: 100,
      render: (v) =>
        Number(v).toLocaleString('es-CO', { maximumFractionDigits: 6 }),
    },
    {
      title: `→ Unidad inventario (destino)`,
      render: (_, r) =>
        `${r.destinoNombre ?? '—'}${r.destinoAbrev ? ` (${r.destinoAbrev})` : ''}`,
    },
    {
      title: '',
      width: 60,
      render: (_, r) => (
        <Button
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteConversion(r.id)}
        />
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────
  if (pageLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 80 }}>
        <Spin tip="Cargando producto..." size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>

      {/* Encabezado */}
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/dashboard/producto/lista')}
        >
          Volver
        </Button>
        <Title level={4} style={{ color: '#1890ff', margin: 0 }}>
          Editar producto
        </Title>
      </Space>

      {/* Alerta unidad bloqueada */}
      {unidadBloqueada && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 20 }}
          message="Unidad de inventario bloqueada"
          description={
            `Este producto tiene ${inventarioCantidad.toLocaleString('es-CO')} unidades en inventario. ` +
            `Cambiar la unidad podría dejar el historial inconsistente, por eso el campo está deshabilitado.`
          }
        />
      )}

      {/* ─── Formulario producto ─── */}
      <Form form={form} layout="vertical" onFinish={handleSave}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="txtNombre"
              label="Nombre"
              rules={[{ required: true, message: 'Ingresa el nombre' }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="txtCodbarras" label="Código de barras">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          {/* Categoría */}
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Categoría">
              <Space>
                <Form.Item name="ddCategoria" noStyle rules={[{ required: true }]}>
                  <ControlSelect
                    options={categorias}
                    placeholder="Seleccionar"
                    showSearch
                    optionFilterProp="label"
                    style={{ flex: 1 }}
                  />
                </Form.Item>
                <AddButton onClick={() => setAddModal({ visible: true, tipo: 'categoria' })} />
              </Space>
            </Form.Item>
          </Col>

          {/* Marca */}
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Marca">
              <Space>
                <Form.Item name="ddMarca" noStyle rules={[{ required: true }]}>
                  <ControlSelect
                    options={marcas}
                    placeholder="Seleccionar"
                    showSearch
                    optionFilterProp="label"
                    style={{ flex: 1 }}
                  />
                </Form.Item>
                <AddButton onClick={() => setAddModal({ visible: true, tipo: 'marca' })} />
              </Space>
            </Form.Item>
          </Col>

          {/* Unidad de inventario */}
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label={
                <Space size={4}>
                  Unidad de inventario
                  <Tooltip
                    title={
                      unidadBloqueada
                        ? 'Bloqueada porque existe inventario. Para cambiarla primero lleva el stock a 0.'
                        : 'Unidad en que se almacena la cantidad en inventario.'
                    }
                  >
                    <InfoCircleOutlined
                      style={{ color: unidadBloqueada ? '#faad14' : '#1890ff' }}
                    />
                  </Tooltip>
                </Space>
              }
            >
              <Form.Item name="ddUnidadBase" noStyle rules={[{ required: true }]}>
                <Select
                  options={unidadesAgrupadas}
                  placeholder="Seleccionar unidad"
                  showSearch
                  optionFilterProp="label"
                  disabled={unidadBloqueada}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Form.Item>
          </Col>

          {/* Código */}
          <Col xs={24} sm={6} md={4}>
            <Form.Item name="txtCodigo" label="Código">
              <Input />
            </Form.Item>
          </Col>

          {/* Centro de costo */}
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Centro de costo">
              <Form.Item name="ddCentroCosto" noStyle rules={[{ required: true }]}>
                <ControlSelect
                  options={centrosCosto}
                  placeholder="Seleccionar"
                  showSearch
                  optionFilterProp="label"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Form.Item>
          </Col>

          {/* Campos visibles solo para Alimentos (categoría 6) */}
          {showPlanDesc && (
            <>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="ddTipoPlanDescuento" label="Tipo de descuento">
                  <ControlSelect
                    options={[
                      { value: 1, label: 'Diario' },
                      { value: 2, label: 'Peso' },
                    ]}
                    placeholder="Seleccionar"
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>
              {showPresentacion && (
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="ddPresentacion" label="Presentación">
                    <ControlSelect
                      options={presentaciones}
                      placeholder="Seleccionar"
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="txtPlanDescuento" label="Descuento inventario">
                  <Input placeholder="Ej: 10 (kg/día) o 30 (días)" />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>

        <Form.Item style={{ marginTop: 8 }}>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={saving}
          >
            Guardar cambios
          </Button>
        </Form.Item>
      </Form>

      {/* ─── Conversiones de unidad ─── */}
      <Card
        style={{ marginTop: 32 }}
        title={
          <Space>
            Conversiones de unidad
            <Tooltip
              title={`Define cuántos "${unidadBaseNombre}" equivale 1 unidad de compra. Ej: 1 Bulto = 40 Kg, 1 Caja = 24 Unidades.`}
            >
              <InfoCircleOutlined style={{ color: '#1890ff' }} />
            </Tooltip>
          </Space>
        }
        extra={
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setConvModalOpen(true)}
          >
            Agregar
          </Button>
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Unidad de inventario: <strong>{unidadBaseNombre}</strong>.
          Registra aquí todas las presentaciones en que se puede comprar o usar este producto.
        </Text>

        <Table
          dataSource={conversiones}
          columns={convColumns}
          rowKey="id"
          loading={convLoading}
          pagination={false}
          size="small"
          locale={{
            emptyText:
              'Sin conversiones. Presiona "Agregar" para definir cómo se compra este producto.',
          }}
        />
      </Card>

      {/* ─── Modal agregar conversión ─── */}
      <Modal
        open={convModalOpen}
        title="Agregar conversión de unidad"
        okText="Guardar"
        cancelText="Cancelar"
        confirmLoading={convSaving}
        onOk={() => convForm.submit()}
        onCancel={() => { setConvModalOpen(false); convForm.resetFields(); }}
        destroyOnClose
      >
        <Form form={convForm} layout="vertical" onFinish={handleAddConversion}>
          {/* Destino fijo = unidad base del producto */}
          <Form.Item label="Unidad de inventario (destino — fijo)">
            <Input value={unidadBaseNombre} disabled />
          </Form.Item>

          <Form.Item
            name="ddUnidadOrigen"
            label="Unidad de compra (origen)"
            rules={[{ required: true, message: 'Selecciona la unidad de compra' }]}
            extra="Unidad en que adquieres o usas el producto (ej: Bulto, Caja, Galón, Libra)."
          >
            <Select
              options={unidadesFlat.filter(u => u.value !== unidadBaseId)}
              placeholder="Seleccionar..."
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="factor"
            label={`Factor  (¿cuántos "${unidadBaseNombre}" hay en 1 unidad de compra?)`}
            rules={[
              { required: true, message: 'Ingresa el factor de conversión' },
              { type: 'number', min: 0.000001, message: 'Debe ser mayor a 0' },
            ]}
            extra="Ej: 1 Bulto = 40 Kg → Factor 40 | 1 Caja = 24 Unidades → Factor 24"
          >
            <InputNumber
              min={0.000001}
              step={1}
              precision={6}
              placeholder="Ej: 40"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal agregar categoría / marca */}
      <AddModal
        visible={addModal.visible}
        title={addModal.tipo === 'categoria' ? 'Agregar Categoría' : 'Agregar Marca'}
        onCancel={() => setAddModal({ visible: false, tipo: '' })}
        onAdd={handleAdd}
      />
    </div>
  );
};

export default ProductoEditar;
